import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { rtkApi, GET_REPORTS } from '@/shared/api/rtkApi';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import {
  GetReportsParams,
  NewReportsCountResponse,
  ReportDetailResponse,
  ReportsListResponse,
  ResolveReportRequest,
  ResolveReportResponse,
  NewStaffRequestsCountResponse,
  NewStudentRequestsCountResponse,
  ResolveStaffRequestParams,
  ResolveStaffRequestResponse,
  ResolveStudentRequestParams,
  ResolveStudentRequestResponse,
  StaffRequestDetailResponse,
  StaffRequestsListResponse,
  StaffRequestsParams,
  StudentRequestDetailResponse,
  StudentRequestsListResponse,
  StudentRequestsParams,
} from './types';

type ResolveMutationResult<T> =
  | { data: T; error?: undefined; meta?: unknown }
  | { data?: undefined; error: FetchBaseQueryError; meta?: unknown };

const buildResolveBody = (params: {
  status: string;
  adminComment: string;
  lessons?: unknown;
  adminAttachments?: File[];
}) => {
  if (!params.adminAttachments?.length) {
    return {
      status: params.status,
      adminComment: params.adminComment,
      ...(params.lessons ? { lessons: params.lessons } : {}),
    };
  }

  const formData = new FormData();
  formData.append('status', params.status);
  formData.append('adminComment', params.adminComment);
  if (params.lessons) {
    formData.append('lessons', JSON.stringify(params.lessons));
  }
  params.adminAttachments?.forEach((file) => formData.append('adminAttachments', file));
  return formData;
};

const resolveRequest = async <T>(
  url: string,
  params: {
    status: string;
    adminComment: string;
    lessons?: unknown;
    adminAttachments?: File[];
  },
): Promise<ResolveMutationResult<T>> => {
  const body = buildResolveBody(params);
  const headers = new Headers({ Accept: 'application/json' });
  const accessToken = LocalStorageService.getAccessToken();

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  if (!(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  try {
    const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
    const response = await fetch(`${apiUrl}${url}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
    const text = await response.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text) as unknown;
      } catch {
        return {
          error: {
            status: 'PARSING_ERROR' as const,
            originalStatus: response.status,
            data: text,
            error: 'Failed to parse response',
          },
        };
      }
    }

    if (!response.ok) {
      return {
        error: {
          status: response.status,
          data,
        },
      };
    }

    return { data: data as T };
  } catch (error) {
    return {
      error: {
        status: 'FETCH_ERROR' as const,
        error: error instanceof Error ? error.message : 'Failed to resolve request',
      },
    };
  }
};

export const reportsApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getReports: build.query<ReportsListResponse, GetReportsParams>({
      query: (params) => ({
        url: '/admin/reports',
        params,
      }),
      providesTags: [GET_REPORTS],
    }),
    getNewReportsCount: build.query<NewReportsCountResponse, void>({
      query: () => ({
        url: '/admin/reports/new/count',
      }),
      providesTags: [GET_REPORTS],
    }),
    getReport: build.query<ReportDetailResponse, number>({
      query: (reportId) => ({
        url: `/admin/reports/${reportId}`,
      }),
      providesTags: [GET_REPORTS],
    }),
    resolveReport: build.mutation<ResolveReportResponse, ResolveReportRequest>({
      query: ({ reportId, status, reportAction }) => ({
        url: `/admin/reports/${reportId}/resolve`,
        method: 'POST',
        body: {
          status,
          reportAction,
        },
      }),
      invalidatesTags: [GET_REPORTS],
    }),
    getStudentRequests: build.query<StudentRequestsListResponse, StudentRequestsParams>({
      query: (params) => ({
        url: '/admin/student-requests',
        params,
      }),
      providesTags: [GET_REPORTS],
    }),
    getNewStudentRequestsCount: build.query<NewStudentRequestsCountResponse, void>({
      query: () => ({
        url: '/admin/student-requests/new/count',
      }),
      providesTags: [GET_REPORTS],
    }),
    getStudentRequest: build.query<StudentRequestDetailResponse, number>({
      query: (requestId) => ({
        url: `/admin/student-requests/${requestId}`,
      }),
      providesTags: [GET_REPORTS],
    }),
    resolveStudentRequest: build.mutation<ResolveStudentRequestResponse, ResolveStudentRequestParams>({
      queryFn: ({ requestId, ...params }) =>
        resolveRequest<ResolveStudentRequestResponse>(
          `/admin/student-requests/${requestId}/resolve`,
          params,
        ),
      invalidatesTags: [GET_REPORTS],
    }),
    getStaffRequests: build.query<StaffRequestsListResponse, StaffRequestsParams>({
      query: (params) => ({
        url: '/admin/staff-requests',
        params,
      }),
      providesTags: [GET_REPORTS],
    }),
    getNewStaffRequestsCount: build.query<NewStaffRequestsCountResponse, void>({
      query: () => ({
        url: '/admin/staff-requests/new/count',
      }),
      providesTags: [GET_REPORTS],
    }),
    getStaffRequest: build.query<StaffRequestDetailResponse, number>({
      query: (requestId) => ({
        url: `/admin/staff-requests/${requestId}`,
      }),
      providesTags: [GET_REPORTS],
    }),
    resolveStaffRequest: build.mutation<ResolveStaffRequestResponse, ResolveStaffRequestParams>({
      queryFn: ({ requestId, ...params }) =>
        resolveRequest<ResolveStaffRequestResponse>(
          `/admin/staff-requests/${requestId}/resolve`,
          params,
        ),
      invalidatesTags: [GET_REPORTS],
    }),
  }),
});

export const {
  useGetReportsQuery,
  useGetNewReportsCountQuery,
  useGetReportQuery,
  useResolveReportMutation,
  useGetStudentRequestsQuery,
  useGetNewStudentRequestsCountQuery,
  useGetStudentRequestQuery,
  useResolveStudentRequestMutation,
  useGetStaffRequestsQuery,
  useGetNewStaffRequestsCountQuery,
  useGetStaffRequestQuery,
  useResolveStaffRequestMutation,
} = reportsApi;
